import { DecimalPipe, DOCUMENT, NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import { afterNextRender, ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, inject, Injector, input, signal, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ActivityEntityName, PriceUtility, TaxInclusiveOption } from '@jms/model';
import { TooltipDirective, TooltipModule } from '@jms/ui/ui.tooltip';
import { WorkBuddyDialogRef } from '@workbuddy/ui/v2/blocks/dialog';
import { ENTITY_BOARD_DIALOG_OUTLET } from '@workbuddy/ui/v2/blocks/entity.board';
// Deep path, not the barrel: entity.dialog mounts the inspector renderer, which mounts this surface, so
// the barrel would close a cycle. The contracts file imports nothing from the surface engine.
import { EntityDialogs } from '@workbuddy/ui/v2/blocks/entity.dialog/entity.dialog.contracts';
// Deep path for the same reason: the entity.record barrel mounts the record page, which mounts this surface.
import { copyTextInsideFocusRoot, EntityRecordCopy, resolveEntityRecordCopy } from '@workbuddy/ui/v2/blocks/entity.record/entity.record.copy';
import { EntityManifestService } from '@workbuddy/services/entities/entity.manifest.service';
import { CurrencyFormatPipe } from '@jms/utils/util.pipes';
import { PAGE_COMPONENTS, PageHeaderSize, PageMode } from '@workbuddy/ui/blocks/page';
import { BadgeComponent } from '@workbuddy/ui/v2/components/badge';
import { DocZoomFrameComponent, DocZoomState } from '@workbuddy/ui/components/doc.zoom.frame';
import { ActionBarComponent, WbActionCommand } from '@workbuddy/ui/v2/components/action.bar';
import { ButtonDisplayStyle } from '@workbuddy/ui/v2/components/button';
import { SelectComponent } from '@workbuddy/ui/v2/fields/select';
import { FieldError, FieldMode } from '@workbuddy/ui/v2/surface.engine/core/contracts/field.binding';
import type { RelationBoardMount } from '@workbuddy/ui/v2/surface.engine/core/contracts/relation.board';
import { CompiledRepeat, CompiledTab } from '@workbuddy/ui/v2/surface.engine/core/runtime/compiled.form';
import { readModelValue } from '@workbuddy/ui/v2/surface.engine/core/runtime/model.path';
import { SurfaceContextHandle } from '@workbuddy/ui/v2/surface.engine/core/runtime/surface.context/create.surface.context.options';
import { WbFieldComponent } from '@workbuddy/ui/v2/surface.engine/core/field/wb.field.component';
import { WbFieldGroupComponent } from '@workbuddy/ui/v2/surface.engine/core/field.group/wb.field.group.component';
import { repeatTableFromCompiledRepeat } from '@workbuddy/ui/v2/surface.engine/core/repeat.table/repeat.table.adapter';
import { RepeatTableConfig } from '@workbuddy/ui/v2/surface.engine/core/repeat.table/repeat.table.model';
import { WbRepeatTableComponent } from '@workbuddy/ui/v2/surface.engine/core/repeat.table/wb.repeat.table.component';
import { WbRepeatSectionsComponent } from '@workbuddy/ui/v2/surface.engine/core/repeat/wb.repeat.sections.component';
import { WbSurfaceRailComponent } from '@workbuddy/ui/v2/surface.engine/core/rail/wb.surface.rail.component';
import { SectionFieldDeclaration, SectionFieldGroupDeclaration, visibleFieldDeclarations, WbSectionComponent } from '@workbuddy/ui/v2/surface.engine/core/sections/wb.section.component';
import { resolveSurfaceActions, toSurfaceActionCommands } from '@workbuddy/ui/v2/surface.engine/core/surface/surface.action.presenter';
import { resolveSurfaceBanner, resolveSurfaceHeaderSlot, resolveSurfaceHeaderTag, resolveSurfaceTotalsFooter, SurfaceHeaderTag } from '@workbuddy/ui/v2/surface.engine/core/surface/surface.chrome.presenter';
import { SurfaceMountMemory } from '@workbuddy/ui/v2/surface.engine/core/surface/surface.mount.memory';
import { SectionRenderInfo } from '@workbuddy/ui/v2/surface.engine/core/surface/surface.section.render.info';
import { surfaceCapabilityFields } from '@workbuddy/ui/v2/surface.engine/core/surface/surface.capability.presenter';
import { WbSurfaceCapabilityRegionComponent } from '@workbuddy/ui/v2/surface.engine/core/surface/wb.surface.capability.region.component';
import { buildSectionRenderMap, sectionsForPaths } from '@workbuddy/ui/v2/surface.engine/core/surface/surface.section.presenter';
import { countSurfaceTabErrors, createRelationMountCache, isSurfaceTabDirty, resolveCustomTabMount, SurfaceCustomTabMount } from '@workbuddy/ui/v2/surface.engine/core/surface/surface.tab.presenter';
import { WbTabDeclaration } from '@workbuddy/ui/v2/surface.engine/core/tabs/wb.tab.shell.component';
import { ViewStateBag } from '@workbuddy/ui/v2/view.state';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { ToastService } from '@workbuddy/ui/services';
import { SurfaceEvent } from '@workbuddy/ui/v2/surface.engine/core/runtime/surface.events';
// Deep paths, not the package barrel: workbuddy/surfaces/financial-document/index.ts pulls in the
// entity surface factories, which import this host back. These two files import nothing from the engine.
import { financialDocumentPartyInitials, financialDocumentPartyView } from '@workbuddy/surfaces/financial-document/financial.document.party.view';
import { financialDocumentHeading, financialDocumentLabel, financialDocumentPairSummary, financialDocumentPrepaymentSummary, isGrandTotalBind } from '@workbuddy/surfaces/financial-document/financial.document.summary.policy';
import { clusterFinancialDocumentActionCommands } from './financial.document.action.cluster';
import { createFinancialDocumentRailState, FinancialDocumentRailSeed, financialDocumentRailLayout, financialDocumentRailTab, nextRememberedRailTab } from './financial.document.rail.layout';
import { WbFinancialDocumentAdvancedPanelComponent } from './wb.financial.document.advanced.panel.component';
import { financialDocumentDeliveryColumns } from './financial.document.delivery.layout';
import { financialDocumentReferenceTooltip, isFinancialDocumentReferenceBind } from './financial.document.reference';
import { financialDocumentHostBridgeStyles, financialDocumentHostStyleId } from './wb.surface.financial.document.host.theme';

const RAIL_MEMORY_SLOT = 'financial-document.rail';
const RAIL_TAB_MEMORY_SLOT = 'financial-document.rail.tab';
const ZOOM_MEMORY_SLOT = 'financial-document.zoom';

const TAX_MODE_OPTIONS: readonly { label: string; value: TaxInclusiveOption }[] = [
  { label: TaxInclusiveOption.Exclusive, value: TaxInclusiveOption.Exclusive },
  { label: TaxInclusiveOption.Inclusive, value: TaxInclusiveOption.Inclusive },
  { label: TaxInclusiveOption.NoTax, value: TaxInclusiveOption.NoTax },
];

const TAX_MODE_BINDS = new Set(['taxInclusiveOption']);
const TOTAL_AMOUNT_BINDS = new Set(['subTotal', 'tax', 'total', 'amount', 'grandTotal']);

interface RailTabRender {
  readonly tab: CompiledTab;
  readonly declaration: WbTabDeclaration;
  readonly fields: readonly SectionFieldDeclaration[];
  readonly itemSections: readonly SectionRenderInfo[];
  readonly mount?: RelationBoardMount;
  readonly custom?: SurfaceCustomTabMount;
}

// Financial document host — Invoice / Bill / Quote / SO / PO.
// Document body slots: meta/parties/delivery/leftover .lines fields/lines repeat/notes/totals.
// Right rail: schema `.tabs()` (form / relation / custom). Legacy `advanced` slot still maps to a Details panel when no tabs are authored.
@Component({
  selector: 'wb-surface-financial-document-host',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [PAGE_COMPONENTS, BadgeComponent, DocZoomFrameComponent, WbFieldComponent, WbFieldGroupComponent, WbRepeatTableComponent, WbRepeatSectionsComponent, WbSectionComponent, CurrencyFormatPipe, DecimalPipe, SelectComponent, FormsModule, NgTemplateOutlet, NgComponentOutlet, ActionBarComponent, WbSurfaceRailComponent, WbFinancialDocumentAdvancedPanelComponent, TooltipModule, WbSurfaceCapabilityRegionComponent],
  templateUrl: './wb.surface.financial.document.host.component.html',
  styleUrls: ['./wb.surface.financial.document.host.component.less', './wb.surface.financial.document.rail.less'],
  host: {
    '[attr.data-surface]': '"financialDocument"',
    '[attr.data-mode]': '$mode()',
  },
})
export class WbSurfaceFinancialDocumentHostComponent {

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  // Both optional: this surface renders in a board's dialog outlet, a shell dialog, an imperative
  // WorkBuddyDialogService dialog, and full page. Each container provides only its own handle, so a
  // missing one means "not that container" rather than a misconfiguration.
  private readonly entityDialogs = inject(EntityDialogs, { optional: true });
  private readonly dialogRef = inject(WorkBuddyDialogRef, { optional: true });
  private readonly injector = inject(Injector);
  private readonly document = inject(DOCUMENT);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);
  /** Optional: only a host that owns a lifetime provides one. Absent means tab state is discarded. */
  private readonly viewStateBag = inject(ViewStateBag, { optional: true });
  private readonly toast = inject(ToastService);
  private readonly manifests = inject(EntityManifestService);
  private readonly relationMounts = createRelationMountCache({ injector: this.injector, viewStateBag: this.viewStateBag ?? undefined, logScope: 'surface.host.financial-document' });

  public $handle = input.required<SurfaceContextHandle>({ alias: 'handle' });

  protected readonly PageMode = PageMode;
  protected readonly PageHeaderSize = PageHeaderSize;
  // Authored sheet width. Locked preview scales this page to the canvas (PDF-style) — it does not reflow.
  protected readonly documentWidth = 1400;
  protected readonly taxModeOptions = TAX_MODE_OPTIONS;
  /** Optional: a container that remounts per record provides one, so the next record opens on the same rail and zoom. */
  private readonly mountMemory = inject(SurfaceMountMemory, { optional: true });
  private readonly $rememberedRailTab = signal<string | undefined>(this.mountMemory?.read<string>(RAIL_TAB_MEMORY_SLOT));
  protected readonly zoomSeed = this.mountMemory?.read<DocZoomState>(ZOOM_MEMORY_SLOT);
  protected readonly railState = createFinancialDocumentRailState(this.mountMemory
    ? { read: () => this.mountMemory?.read<FinancialDocumentRailSeed>(RAIL_MEMORY_SLOT), write: seed => this.mountMemory?.write(RAIL_MEMORY_SLOT, seed) }
    : undefined);
  protected readonly $railDockMode = this.railState.docked;
  protected readonly $railPinned = this.railState.pinned;
  protected readonly $railCollapsed = this.railState.collapsed;

  public constructor() {

    DesignSystem.Mount(this.document, financialDocumentHostStyleId, financialDocumentHostBridgeStyles);

    effect(onCleanup => {
      const off = this.$handle().page.eventBus.on(SurfaceEvent.SubmitFailed, payload => {
        this.toast.error('Cannot save', this.submitErrorMessage(payload.errors[0]));
      });
      onCleanup(off);
    });

    afterNextRender({ earlyRead: () => {
      const host = this.elementRef.nativeElement;
      // Zoneless: a ResizeObserver write renders a frame after paint, so the first paint would show the
      // wrong dock state. Measuring here re-renders before paint; the locked preview's zoom frame forces this layout anyway.
      this.railState.resize(host.clientWidth);
      const observer = new ResizeObserver(() => this.railState.resize(host.clientWidth));
      observer.observe(host);
      this.destroyRef.onDestroy(() => observer.disconnect());
    } });
  }

  private submitErrorMessage(error?: FieldError): string {

    if (!error) return 'Check required fields.';
    const label = error.path
      ? this.$compiled().fields.find(field => field.bind === error.path)?.metadata.displayName ?? error.path
      : undefined;
    if (label && (error.code === 'required' || error.message === 'This field is required.')) return `${label} is required.`;
    return error.message;
  }

  protected readonly $page = computed(() => this.$handle().page);
  protected readonly $compiled = computed(() => this.$handle().compiled);
  protected readonly $model = computed(() => this.$page().$model());
  protected readonly $surfaceErrors = computed(() => this.$page().$surfaceErrors());
  protected readonly $readonly = computed(() => this.$page().$readonly());
  protected readonly $mode = computed(() => this.$page().$mode?.() ?? 'edit');
  protected readonly $saving = computed(() => this.$page().$saving());

  protected readonly $surfaceMode = computed<FieldMode>(() => {
    if (this.$mode() === 'preview' || this.$readonly()) return 'read';
    return this.$handle().surface.defaultFieldMode;
  });

  protected isReferenceBind(bind: string): boolean {

    return isFinancialDocumentReferenceBind(bind);
  }

  /** Tooltip text only in readonly. Whether it shows depends on the text actually overflowing. */
  protected referenceTooltip(bind: string): string | null {

    return financialDocumentReferenceTooltip(this.$surfaceMode(), bind, readModelValue(this.$model(), bind));
  }

  protected showReferenceTooltip(tooltip: TooltipDirective, event: Event, bind: string): void {

    if (!this.referenceTooltip(bind)) return;
    const slot = event.currentTarget;
    if (!(slot instanceof HTMLElement)) return;
    const label = slot.querySelector('.content-label');
    if (!(label instanceof HTMLElement) || label.scrollWidth <= label.clientWidth + 1) return;
    tooltip.show();
  }

  // ── Header chrome ──────────────────────────────────────────────────────────
  protected readonly $title = computed(() => {
    const entity = this.documentLabel();
    const mode = this.$mode();
    return mode === 'create' ? `New ${entity}` : entity;
  });

  protected readonly $showClose = computed(() => this.$mode() === 'create' || this.$mode() === 'edit' || !!this.entityDialogs?.$active() || !!this.dialogRef);

  /**
   * Closes whichever container is hosting this surface.
   *
   * Ordered most specific first, because only the container itself knows how to unwind cleanly. The
   * board-outlet walk and URL rewrite below are route-shaped and **no-op** when the surface was mounted
   * imperatively or into the shell's dialog outlet — which is why a dialog opened through
   * `dialogService.page()` could previously only be dismissed with Escape.
   *
   * `close()` on the entity dialog runs its leave check, so Cancel with unsaved content prompts rather
   * than discarding.
   */
  protected async onClose(): Promise<void> {

    if (this.entityDialogs?.$active()) {
      await this.entityDialogs.close();
      return;
    }

    if (this.dialogRef) {
      void this.dialogRef.close();
      return;
    }

    let current: ActivatedRoute | null = this.route;
    while (current) {
      if (current.outlet === ENTITY_BOARD_DIALOG_OUTLET) {
        void this.router.navigate(
          [{ outlets: { [ENTITY_BOARD_DIALOG_OUTLET]: null } }],
          { relativeTo: current.parent ?? current, queryParamsHandling: 'preserve', replaceUrl: true },
        );
        return;
      }
      current = current.parent;
    }

    void this.router.navigateByUrl(this.router.url.replace(/\/\(dialog:[^)]+\)/, ''));
  }

  protected readonly $documentNumber = computed(() => {
    const model = this.$model();
    const identifier = this.$compiled().header?.identifier;
    if (identifier) {
      const value = readModelValue(model, identifier);
      if (value != null && value !== '') return String(value);
    }
    return String(model['invoiceNumber'] ?? model['orderNumber'] ?? model['number'] ?? model['quoteNumber'] ?? '—');
  });
  /** The saved record's shareable page; null for a new document, which has no URL yet. */
  private readonly $recordUrl = computed(() => {
    const id = this.$model()['_id'];
    if (this.$mode() === 'create' || typeof id !== 'string' || !id) return null;
    return this.manifests.routes(this.$compiled().entity as ActivityEntityName)?.record?.(id) ?? null;
  });
  protected readonly $canCopyRecord = computed(() => resolveEntityRecordCopy(this.$recordUrl(), '', null, false) !== null);
  protected readonly $recordCopied = signal<EntityRecordCopy | null>(null);
  protected readonly $copyRecordTitle = computed(() => {
    const copied = this.$recordCopied();
    if (copied) return copied.kind === 'number' ? `Copied ${copied.text}` : 'Link copied';
    const number = this.$documentNumber();
    return number === '—' ? 'Copy link' : `Copy link (Shift: copy ${number})`;
  });
  private recordCopiedTimer: ReturnType<typeof setTimeout> | undefined;
  protected readonly $documentHeading = computed(() => financialDocumentHeading(this.$compiled().entity, this.$documentNumber()));
  protected readonly $headerTags = computed<readonly SurfaceHeaderTag[]>(() => {
    const header = this.$compiled().header;
    if (!header) return [];
    const model = this.$model();
    const resolver = this.$handle().resolver;
    return header.tags.map(tag => resolveSurfaceHeaderTag(tag, model, resolver)).filter((tag): tag is SurfaceHeaderTag => !!tag);
  });

  // Optional banner (alert strips) under the header, resolved from the runtime chain like the
  // manage and settings hosts. Every FD entity gets the slot; only one that declares `banner` fills it.
  protected readonly $banner = computed(() => resolveSurfaceBanner(this.$handle().resolver));

  // Runtime-contributed header controls, mounted beside the title. The host does not know what they
  // are — same contract the detail host honours, so an entity adds one without touching this file.
  protected readonly $headerSlot = computed(() => resolveSurfaceHeaderSlot(this.$handle().resolver));
  protected readonly $totalsFooter = computed(() => resolveSurfaceTotalsFooter(this.$handle().resolver));

  private readonly $resolvedCommands = computed(() => resolveSurfaceActions(this.$handle()));

  // One command at a time: while any action runs, every action is disabled so a
  // double-click cannot dispatch the same backend command twice (convert, cancel, …).
  private readonly $commandRunning = signal(false);

  private guardCommandRun(run: WbActionCommand['run']): WbActionCommand['run'] {
    return async () => {
      if (this.$commandRunning()) return;
      this.$commandRunning.set(true);
      try {
        await run?.();
      } finally {
        this.$commandRunning.set(false);
      }
    };
  }

  protected readonly $actionCommands = computed<WbActionCommand[]>(() => {
    const visible = toSurfaceActionCommands(this.$resolvedCommands(), this.$page().$saving() || this.$commandRunning())
      .map(command => ({
        ...command,
        displayStyle: ButtonDisplayStyle.Secondary,
        run: this.guardCommandRun(command.run),
      }));

    return clusterFinancialDocumentActionCommands(visible);
  });

  // ── Named surface slots ────────────────────────────────────────────────────
  private readonly $sectionByPath = computed(() => {
    return buildSectionRenderMap(this.$handle());
  });
  protected readonly $capabilityFields = computed(() => surfaceCapabilityFields(this.$handle().compiled, this.$sectionByPath(), this.$handle().page));

  private sectionsForSlot(slot: string): SectionRenderInfo[] {
    const paths = this.$compiled().namedSlotSections?.[slot] ?? [];
    return [...sectionsForPaths(paths, this.$sectionByPath())];
  }

  private fieldsForSlot(slot: string): SectionFieldDeclaration[] {
    return this.sectionsForSlot(slot).flatMap(s => s.fields);
  }

  private fieldGroupsForSlot(slot: string): SectionFieldGroupDeclaration[] {
    return this.sectionsForSlot(slot).flatMap(section => section.fieldGroups);
  }

  private sectionsForTab(tab: CompiledTab): readonly SectionRenderInfo[] {

    return sectionsForPaths(tab.sections, this.$sectionByPath());
  }

  protected readonly $metaFields = computed(() => this.fieldsForSlot('meta'));
  protected readonly $partyFields = computed(() => this.fieldsForSlot('parties'));
  protected readonly $partyFieldGroups = computed(() => this.fieldGroupsForSlot('parties'));
  protected readonly $deliveryColumns = computed(() => financialDocumentDeliveryColumns(this.sectionsForSlot('delivery')));
  protected readonly $notesFields = computed(() => this.fieldsForSlot('notes'));
  protected readonly $advancedFields = computed(() => this.fieldsForSlot('advanced'));

  protected readonly $totalsFields = computed(() => this.fieldsForSlot('totals'));
  protected readonly $taxModeField = computed(() => this.$totalsFields().find(f => TAX_MODE_BINDS.has(f.field.bind)));
  protected readonly $amountFields = computed(() =>
    visibleFieldDeclarations(this.$totalsFields().filter(f => !TAX_MODE_BINDS.has(f.field.bind) && (TOTAL_AMOUNT_BINDS.has(f.field.bind) || f.metadata?.type === 'Currency'))),
  );
  private readonly $amountBinds = computed<readonly string[]>(() => this.$amountFields().map(f => f.field.bind));
  protected readonly $pairRows = computed(() => financialDocumentPairSummary(this.$amountBinds()));
  // Derived from the model rather than a totals field: `prepaymentInc` is server-owned and carries
  // its own enablement gate, so declaring it as an amount bind would make it a fourth Buy|Sell row.
  protected readonly $prepayment = computed(() => financialDocumentPrepaymentSummary(this.$model()));
  protected readonly $profitMetrics = computed(() => {
    if (!this.$pairRows()) return null;
    const buy = Number(this.$model()['totalBuy']) || 0;
    const sell = Number(this.$model()['totalSellEx'] ?? this.$model()['totalEx'] ?? this.$model()['subTotal']) || 0;
    return { profit: sell - buy, margin: PriceUtility.calculateMargin(buy, sell), markup: PriceUtility.calculateMarkup(buy, sell) };
  });

  // ── Right rail (schema tabs; fallback to legacy advanced slot) ──────────────
  private readonly $railTabRenders = computed<readonly RailTabRender[]>(() => {
    const compiled = this.$compiled();
    const authored = compiled.tabs ?? [];

    if (authored.length) {
      return authored.map(tab => {
        const sections = tab.type === 'form' ? this.sectionsForTab(tab) : [];
        return {
          tab,
          declaration: { value: tab.path, label: this.$page().tab(tab.path).$title() ?? tab.title, visible: !this.$page().tab(tab.path).$hidden(), errorCount: countSurfaceTabErrors(this.$page(), tab, sections), dirty: isSurfaceTabDirty(this.$page(), tab, sections) },
          fields: sections.flatMap(section => section.fields),
          itemSections: sections,
          mount: tab.type === 'relation' ? this.relationMounts.resolve(tab, this.$handle()) : undefined,
          custom: tab.type === 'custom' ? resolveCustomTabMount(tab, this.$handle()) : undefined,
        };
      });
    }

    // Legacy: entities that still only author `.advanced(...)` get a single Details panel.
    const legacyFields = this.$advancedFields();
    if (!legacyFields.length) return [];
    return [{
      tab: { path: 'details', title: 'Details', type: 'form', sections: [] },
      declaration: { value: 'details', label: 'Details', visible: true, errorCount: 0 },
      fields: legacyFields,
      itemSections: [],
    }];
  });

  protected readonly $railTabs = computed<readonly WbTabDeclaration[]>(() => this.$railTabRenders().map(item => item.declaration));
  protected readonly $showRail = computed(() => this.$railTabs().length > 0);
  // The document preview fits to the column this leaves it; see financialDocumentRailLayout.
  protected readonly $railLayout = computed(() => financialDocumentRailLayout({ visible: this.$showRail(), docked: this.$railDockMode(), pinned: this.$railPinned(), collapsed: this.$railCollapsed() }));

  protected readonly $activeRailTab = computed(() => financialDocumentRailTab(this.$railTabs(), this.$rememberedRailTab()));

  protected onRailTabChange(selected: string | undefined): void {

    const next = nextRememberedRailTab(this.$railTabs(), this.$rememberedRailTab(), selected);
    this.$rememberedRailTab.set(next);
    if (next !== undefined) this.mountMemory?.write(RAIL_TAB_MEMORY_SLOT, next);
  }

  protected onZoomStateChange(state: DocZoomState): void {

    this.mountMemory?.write(ZOOM_MEMORY_SLOT, state);
  }

  protected railTabOf(tab: WbTabDeclaration | null | undefined): RailTabRender | undefined {

    if (!tab) return this.$railTabRenders()[0];
    return this.$railTabRenders().find(item => item.declaration.value === tab.value) ?? this.$railTabRenders()[0];
  }

  // Non-repeat fields on `.lines` used to be dropped. Paint them above the first section
  // so Quote can bind Top Section (`description`) without a new slot.
  protected readonly $linesIntroFields = computed(() => this.fieldsForSlot('lines'));

  protected readonly $linesRepeat = computed<CompiledRepeat | undefined>(() => {
    for (const info of this.sectionsForSlot('lines')) {
      const nested = info.repeats.find(r => r.layout === 'section-block' || r.layout === 'table');
      if (nested) return nested;
    }
    return undefined;
  });

  protected readonly $isNestedSections = computed(() => this.$linesRepeat()?.layout === 'section-block');

  protected partyView(field: SectionFieldDeclaration): { name: string; phone: string; email: string; label: string } {
    const base = financialDocumentPartyView(this.$model()[field.field.bind]);
    return {
      ...base,
      label: field.metadata.displayName || field.field.bind,
    };
  }

  protected readonly $taxMode = computed(() => {
    const bind = this.$taxModeField()?.field.bind ?? 'taxInclusiveOption';
    return (this.$model()[bind] as TaxInclusiveOption | undefined) ?? TaxInclusiveOption.Exclusive;
  });

  protected amountValue(field: SectionFieldDeclaration): number {
    return Number(this.$model()[field.field.bind]) || 0;
  }

  protected amountByBind(bind: string): number {

    return Number(this.$model()[bind]) || 0;
  }

  protected isTotalRow(field: SectionFieldDeclaration): boolean {

    return isGrandTotalBind(field.field.bind, this.$amountBinds());
  }

  protected setTaxMode(mode: string | TaxInclusiveOption): void {
    if (this.$readonly()) return;
    const value = mode as TaxInclusiveOption;
    if (value !== TaxInclusiveOption.Exclusive && value !== TaxInclusiveOption.Inclusive && value !== TaxInclusiveOption.NoTax) return;
    const field = this.$taxModeField()?.field;
    if (field) field.setValue(value);
    else this.$page().writeBind('taxInclusiveOption', value);
  }

  protected repeatTableConfig(rep: CompiledRepeat): RepeatTableConfig {
    return repeatTableFromCompiledRepeat(rep);
  }

  protected initials(name: string): string {

    return financialDocumentPartyInitials(name);
  }

  protected onCopyRecord(event: MouseEvent): void {

    const number = this.$documentNumber();
    const copy = resolveEntityRecordCopy(this.$recordUrl(), this.document.defaultView?.location.origin ?? '', number === '—' ? null : number, event.shiftKey);
    if (!copy) return;
    void this.placeRecordCopy(copy);
  }

  /** Writes the resolved text from inside this host, which sits inside the Edit dialog's focus trap. */
  private async placeRecordCopy(copy: EntityRecordCopy): Promise<void> {

    const written = await copyTextInsideFocusRoot(this.document, this.elementRef.nativeElement, copy.text);
    if (!written) return;
    this.$recordCopied.set(copy);
    clearTimeout(this.recordCopiedTimer);
    this.recordCopiedTimer = setTimeout(() => this.$recordCopied.set(null), 2000);
  }

  private documentLabel(): string {

    return financialDocumentLabel(this.$compiled().entity);
  }

}
